// astriaIndiaV2Copy.ts
// ─── Astria India v2 — English/Hindi/Tamil/Marathi UI copy ─────────────────
// All fixed UI strings for the shell header, shared wizard/card chrome, and
// each of the 7 tabs' questions/helpers/option labels/result-field labels/
// buttons. The backend reading text itself (message.content /
// astriaIndiaV2Data field values) is NOT translated here — that comes back
// from the model already in the selected language (see
// helper/astriaIndiaV2Service.js's resolveIndiaV2Target). This file only
// localizes the surrounding UI chrome. Same pattern as
// components/astro/indonesiaV2/indonesiaV2Copy.ts.
import { IndiaV2Language } from "./astriaIndiaV2Language";

interface OptionCopy {
  value: string;
  label: string;
}

interface ShellCopy {
  title: string;
  subtitle: string;
  langToggleLabel: string;
}

interface SharedCopy {
  stepOf: (step: number, total: number) => string;
  percentComplete: (pct: number) => string;
  back: string;
  continueLabel: string;
  selectAnOption: string;
  tryAgain: string;
  continueInChat: string;
  whatsApp: string;
  instagram: string;
  copy: string;
  copied: string;
  saveImage: string;
  startNewReading: string;
  emojiLabels: Record<
    "content" | "low" | "anxious" | "frustrated" | "calm" | "sad" | "drained" | "confused",
    string
  >;
}

interface SambandhCopy {
  tabLabel: string;
  tabSublabel: string;
  loading: string;
  resultTitle: string;
  connectionTypeLabel: string;
  connectionTypePlaceholder: string;
  connectionTypes: OptionCopy[];
  theirDetailsLabel: string;
  namePlaceholder: string;
  dobPlaceholder: string;
  timePlaceholder: string;
  placePlaceholder: string;
  step1Question: string;
  step1Helper: string;
  step2Question: string;
  step2Low: string;
  step2High: string;
  step3Question: string;
  toneOptions: OptionCopy[];
  step4Question: string;
  step4Helper: string;
  clarityOptions: OptionCopy[];
  revealButton: string;
  fields: {
    rhythmBetween: string;
    harmonyLevel: string;
    frictionPoint: string;
    timingAlignment: string;
    connectionPath: string;
  };
}

interface VivahCopy {
  tabLabel: string;
  tabSublabel: string;
  loading: string;
  resultTitle: string;
  step1Question: string;
  step1Helper: string;
  brideLabel: string;
  groomLabel: string;
  detailsSuffix: string;
  namePlaceholder: (person: string) => string;
  dobPlaceholder: string;
  timePlaceholder: string;
  placePlaceholder: string;
  step2Question: string;
  step3Question: string;
  readinessLow: string;
  readinessHigh: string;
  step4Question: string;
  rhythmOptions: OptionCopy[];
  preferredTimingLabel: string;
  periodPlaceholder: string;
  periodOptions: OptionCopy[];
  revealButton: string;
  fields: {
    compatibilitySnapshot: string;
    recommendedWindows: string;
    timingToApproachGently: string;
  };
}

interface UpayCopy {
  tabLabel: string;
  tabSublabel: string;
  loading: string;
  resultTitle: string;
  step1Question: string;
  situationOptions: OptionCopy[];
  step2Question: string;
  weightLow: string;
  weightHigh: string;
  step3Question: string;
  outcomeOptions: OptionCopy[];
  revealButton: string;
  fields: {
    currentEnergy: string;
    vedicReflection: string;
    suggestedPractices: string;
  };
}

interface BhavnaCopy {
  tabLabel: string;
  tabSublabel: string;
  loading: string;
  resultTitle: string;
  step1Question: string;
  step2Question: string;
  intensityLow: string;
  intensityHigh: string;
  step3Question: string;
  locationOptions: OptionCopy[];
  revealButton: string;
  fields: {
    emotionalState: string;
    rootPattern: string;
    currentWeight: string;
    innerRoom: string;
    softLanding: string;
    actions: string;
  };
}

interface VyaktitvaCopy {
  tabLabel: string;
  tabSublabel: string;
  loading: string;
  resultTitle: string;
  step1Question: string;
  yourTraits: OptionCopy[];
  step2Question: string;
  theirTraits: OptionCopy[];
  step3Question: string;
  stylePlaceholder: string;
  styleOptions: OptionCopy[];
  revealButton: string;
  fields: {
    coreNature: string;
    emotionalPattern: string;
    innerRhythm: string;
    relationshipStyle: string;
    growthInvitation: string;
    actions: string;
  };
}

interface SamayCopy {
  tabLabel: string;
  tabSublabel: string;
  loading: string;
  resultTitle: string;
  step1Question: string;
  flowOptions: OptionCopy[];
  step2Question: string;
  commsLow: string;
  commsHigh: string;
  step3Question: string;
  directionOptions: OptionCopy[];
  revealButton: string;
}

interface AapkaNoteCopy {
  tabLabel: string;
  tabSublabel: string;
  loading: string;
  resultTitle: string;
  step1Question: string;
  step1HelperCaptured: string;
  step1HelperEmpty: string;
  notePlaceholder: string;
  step2Question: string;
  revealButton: string;
  reflectOnSomethingElse: string;
  yourNoteLabel: string;
  yourNoteFallback: string;
  reflectionLabel: string;
  emotionalThreadLabel: string;
  gentleNoteLabel: string;
}

interface IndiaV2CopyShape {
  shell: ShellCopy;
  shared: SharedCopy;
  sambandh: SambandhCopy;
  vivah: VivahCopy;
  upay: UpayCopy;
  bhavna: BhavnaCopy;
  vyaktitva: VyaktitvaCopy;
  samay: SamayCopy;
  aapkaNote: AapkaNoteCopy;
}

export const ASTRIA_INDIA_V2_COPY: Record<IndiaV2Language, IndiaV2CopyShape> = {
  // ─────────────────────────────────────────── ENGLISH ───────────────────
  en: {
    shell: {
      title: "Astria India",
      subtitle: "An emotional guidance space, gently rooted in Vedic wisdom",
      langToggleLabel: "Language",
    },
    shared: {
      stepOf: (step, total) => `Step ${step} of ${total}`,
      percentComplete: (pct) => `${pct}% complete`,
      back: "Back",
      continueLabel: "Continue",
      selectAnOption: "Select an option",
      tryAgain: "Try Again",
      continueInChat: "Continue in Chat",
      whatsApp: "WhatsApp",
      instagram: "Instagram",
      copy: "Copy",
      copied: "Copied",
      saveImage: "Save Image",
      startNewReading: "Start a new reading",
      emojiLabels: {
        content: "Content",
        low: "Low",
        anxious: "Anxious",
        frustrated: "Frustrated",
        calm: "Calm",
        sad: "Sad",
        drained: "Drained",
        confused: "Confused",
      },
    },
    sambandh: {
      tabLabel: "Sambandh Taal Mel",
      tabSublabel: "Relationship",
      loading: "Reading your relationship rhythm…",
      resultTitle: "Your Relationship Rhythm",
      connectionTypeLabel: "Connection Type",
      connectionTypePlaceholder: "Choose a connection type",
      connectionTypes: [
        { value: "partner", label: "Partner / Spouse" },
        { value: "boyfriend_girlfriend", label: "Boyfriend / Girlfriend" },
        { value: "close_friend", label: "Close Friend" },
        { value: "family", label: "Family Member" },
        { value: "situationship", label: "It's Complicated" },
      ],
      theirDetailsLabel: "Their Details",
      namePlaceholder: "Their name",
      dobPlaceholder: "Date of birth — DD/MM/YYYY",
      timePlaceholder: "Time of birth (optional) — e.g. 10:30 AM",
      placePlaceholder: "Place of birth (optional)",
      step1Question: "Tell us about the two of you",
      step1Helper: "We collect their details once, upfront, so we never have to ask again.",
      step2Question: "How often do you two talk?",
      step2Low: "Rarely",
      step2High: "Constantly",
      step3Question: "What's their usual tone with you?",
      toneOptions: [
        { value: "warm", label: "Warm" },
        { value: "distant", label: "Distant" },
        { value: "unpredictable", label: "Unpredictable" },
        { value: "steady", label: "Steady" },
        { value: "guarded", label: "Guarded" },
      ],
      step4Question: "What would you like clarity on?",
      step4Helper: "Select all that resonate.",
      clarityOptions: [
        { value: "communication", label: "Communication" },
        { value: "commitment", label: "Commitment" },
        { value: "emotional_distance", label: "Emotional Distance" },
        { value: "trust", label: "Trust" },
        { value: "future_direction", label: "Future Direction" },
      ],
      revealButton: "Reveal My Reading",
      fields: {
        rhythmBetween: "Rhythm Between You",
        harmonyLevel: "Harmony Level",
        frictionPoint: "A Gentle Friction Point",
        timingAlignment: "Timing Alignment",
        connectionPath: "Path to Deeper Connection",
      },
    },
    vivah: {
      tabLabel: "Vivah Muhurat",
      tabSublabel: "Timing",
      loading: "Reading your timing window…",
      resultTitle: "Your Timing Window",
      step1Question: "Tell us about the Bride and Groom",
      step1Helper: "We collect both sets of details once, upfront, so we never have to ask again.",
      brideLabel: "Bride",
      groomLabel: "Groom",
      detailsSuffix: "'s Details",
      namePlaceholder: (person) => `${person}'s name`,
      dobPlaceholder: "Date of birth — DD/MM/YYYY",
      timePlaceholder: "Time of birth (optional) — e.g. 10:30 AM",
      placePlaceholder: "Place of birth (optional)",
      step2Question: "How ready does the Bride feel for this union?",
      step3Question: "How ready does the Groom feel?",
      readinessLow: "Not yet",
      readinessHigh: "Fully ready",
      step4Question: "What's the current rhythm between them?",
      rhythmOptions: [
        { value: "steady", label: "Steady & Calm" },
        { value: "intense", label: "Intense & Passionate" },
        { value: "uncertain", label: "Uncertain" },
        { value: "growing", label: "Slowly Growing" },
      ],
      preferredTimingLabel: "Preferred Timing",
      periodPlaceholder: "Choose a period",
      periodOptions: [
        { value: "next_3_months", label: "Next 3 Months" },
        { value: "next_6_months", label: "Next 6 Months" },
        { value: "this_year", label: "This Year" },
        { value: "next_year", label: "Next Year" },
      ],
      revealButton: "Reveal My Timing",
      fields: {
        compatibilitySnapshot: "Compatibility Snapshot",
        recommendedWindows: "Recommended Windows",
        timingToApproachGently: "Timing to Approach Gently",
      },
    },
    upay: {
      tabLabel: "Upay Marg",
      tabSublabel: "Guidance",
      loading: "Finding your practices…",
      resultTitle: "Your Path of Alignment",
      step1Question: "What's weighing on you right now?",
      situationOptions: [
        { value: "stress", label: "Stress" },
        { value: "overthinking", label: "Overthinking" },
        { value: "career_block", label: "Career Block" },
        { value: "relationship_tension", label: "Relationship Tension" },
        { value: "grief", label: "Grief" },
        { value: "transition", label: "Life Transition" },
        { value: "loneliness", label: "Loneliness" },
        { value: "burnout", label: "Burnout" },
      ],
      step2Question: "How heavy does it feel?",
      weightLow: "Light",
      weightHigh: "Very heavy",
      step3Question: "What are you hoping to feel?",
      outcomeOptions: [
        { value: "clarity", label: "Clarity" },
        { value: "calm", label: "Calm" },
        { value: "release", label: "Release" },
        { value: "direction", label: "Direction" },
        { value: "connection", label: "Connection" },
      ],
      revealButton: "Reveal My Guidance",
      fields: {
        currentEnergy: "Current Energy",
        vedicReflection: "Vedic Reflection",
        suggestedPractices: "Suggested Practices",
      },
    },
    bhavna: {
      tabLabel: "Bhavna Drishti",
      tabSublabel: "Emotion Map",
      loading: "Reading your inner weather…",
      resultTitle: "Your Inner Weather",
      step1Question: "How are you feeling right now?",
      step2Question: "How intense is this feeling?",
      intensityLow: "Faint",
      intensityHigh: "Overwhelming",
      step3Question: "Where does it sit?",
      locationOptions: [
        { value: "mind", label: "Mind" },
        { value: "heart", label: "Heart" },
        { value: "both", label: "Both" },
      ],
      revealButton: "Reveal My Emotion Map",
      fields: {
        emotionalState: "Emotional State",
        rootPattern: "Root Pattern",
        currentWeight: "Current Weight",
        innerRoom: "Inner Room",
        softLanding: "Soft Landing",
        actions: "Gentle Steps",
      },
    },
    vyaktitva: {
      tabLabel: "Vyaktitva Darshan",
      tabSublabel: "Personality",
      loading: "Reading your personality pattern…",
      resultTitle: "Your Personality Profile",
      step1Question: "How would you describe yourself?",
      yourTraits: [
        { value: "expressive", label: "Expressive" },
        { value: "reserved", label: "Reserved" },
        { value: "independent", label: "Independent" },
        { value: "caring", label: "Caring" },
        { value: "driven", label: "Driven" },
      ],
      step2Question: "How would you describe them?",
      theirTraits: [
        { value: "warm", label: "Warm" },
        { value: "logical", label: "Logical" },
        { value: "playful", label: "Playful" },
        { value: "guarded", label: "Guarded" },
        { value: "intense", label: "Intense" },
      ],
      step3Question: "What's your usual interaction style?",
      stylePlaceholder: "Choose a style",
      styleOptions: [
        { value: "direct", label: "Direct & Honest" },
        { value: "gentle", label: "Gentle & Careful" },
        { value: "playful", label: "Playful & Light" },
        { value: "deep", label: "Deep & Reflective" },
      ],
      revealButton: "Reveal My Personality",
      fields: {
        coreNature: "Core Nature",
        emotionalPattern: "Emotional Pattern",
        innerRhythm: "Inner Rhythm",
        relationshipStyle: "Relationship Style",
        growthInvitation: "Growth Invitation",
        actions: "Gentle Steps",
      },
    },
    samay: {
      tabLabel: "Samay Pravah",
      tabSublabel: "Flow",
      loading: "Reading your flow…",
      resultTitle: "Your Flow",
      step1Question: "How does your current flow feel?",
      flowOptions: [
        { value: "rising", label: "Rising" },
        { value: "settling", label: "Settling" },
        { value: "scattered", label: "Scattered" },
        { value: "unclear", label: "Not Sure" },
      ],
      step2Question: "How connected have you felt with others lately?",
      commsLow: "Withdrawn",
      commsHigh: "Very connected",
      step3Question: "Which way is your energy moving?",
      directionOptions: [
        { value: "inward", label: "Turning Inward" },
        { value: "outward", label: "Reaching Outward" },
        { value: "steady", label: "Staying Steady" },
      ],
      revealButton: "Reveal My Flow",
    },
    aapkaNote: {
      tabLabel: "Aapka Note",
      tabSublabel: "Reflection",
      loading: "Sitting with your note…",
      resultTitle: "Your Reflection",
      step1Question: "What's on your mind?",
      step1HelperCaptured: "We've captured your latest message — feel free to edit it.",
      step1HelperEmpty: "Write a few words about what you'd like to reflect on.",
      notePlaceholder: "Type or edit your note…",
      step2Question: "How do you feel about this?",
      revealButton: "Reveal My Reflection",
      reflectOnSomethingElse: "Reflect on something else",
      yourNoteLabel: "Your Note",
      yourNoteFallback: "Reflection on your thoughts",
      reflectionLabel: "Reflection",
      emotionalThreadLabel: "Emotional Thread",
      gentleNoteLabel: "Gentle Note",
    },
  },

  // ─────────────────────────────────────────── HINDI ─────────────────────
  hi: {
    shell: {
      title: "आस्त्रिया इंडिया",
      subtitle: "वैदिक ज्ञान में निहित एक भावनात्मक मार्गदर्शन स्थान",
      langToggleLabel: "भाषा",
    },
    shared: {
      stepOf: (step, total) => `चरण ${step} / ${total}`,
      percentComplete: (pct) => `${pct}% पूर्ण`,
      back: "पीछे",
      continueLabel: "जारी रखें",
      selectAnOption: "एक विकल्प चुनें",
      tryAgain: "पुनः प्रयास करें",
      continueInChat: "चैट में जारी रखें",
      whatsApp: "व्हाट्सएप",
      instagram: "इंस्टाग्राम",
      copy: "कॉपी करें",
      copied: "कॉपी हो गया",
      saveImage: "छवि सहेजें",
      startNewReading: "एक नई रीडिंग शुरू करें",
      emojiLabels: {
        content: "संतुष्ट",
        low: "उदास",
        anxious: "चिंतित",
        frustrated: "निराश",
        calm: "शांत",
        sad: "दुखी",
        drained: "थका हुआ",
        confused: "उलझन में",
      },
    },
    sambandh: {
      tabLabel: "संबंध ताल मेल",
      tabSublabel: "रिश्ता",
      loading: "आपके रिश्ते की लय पढ़ी जा रही है…",
      resultTitle: "आपके रिश्ते की लय",
      connectionTypeLabel: "संबंध प्रकार",
      connectionTypePlaceholder: "एक संबंध प्रकार चुनें",
      connectionTypes: [
        { value: "partner", label: "पार्टनर / जीवनसाथी" },
        { value: "boyfriend_girlfriend", label: "बॉयफ्रेंड / गर्लफ्रेंड" },
        { value: "close_friend", label: "करीबी दोस्त" },
        { value: "family", label: "परिवार का सदस्य" },
        { value: "situationship", label: "थोड़ा उलझा हुआ रिश्ता" },
      ],
      theirDetailsLabel: "उनका विवरण",
      namePlaceholder: "उनका नाम",
      dobPlaceholder: "जन्म तिथि — DD/MM/YYYY",
      timePlaceholder: "जन्म समय (वैकल्पिक) — जैसे 10:30 AM",
      placePlaceholder: "जन्म स्थान (वैकल्पिक)",
      step1Question: "हमें आप दोनों के बारे में बताएं",
      step1Helper: "हम उनका विवरण एक बार, पहले ही ले लेते हैं, ताकि हमें दोबारा न पूछना पड़े।",
      step2Question: "आप दोनों कितनी बार बात करते हैं?",
      step2Low: "बहुत कम",
      step2High: "लगातार",
      step3Question: "आपके साथ उनका सामान्य लहजा कैसा है?",
      toneOptions: [
        { value: "warm", label: "गर्मजोशी भरा" },
        { value: "distant", label: "दूर-दूर सा" },
        { value: "unpredictable", label: "अनिश्चित" },
        { value: "steady", label: "स्थिर" },
        { value: "guarded", label: "सतर्क" },
      ],
      step4Question: "आप किस बारे में स्पष्टता चाहते हैं?",
      step4Helper: "जो भी आपको सही लगे, सब चुनें।",
      clarityOptions: [
        { value: "communication", label: "संवाद" },
        { value: "commitment", label: "प्रतिबद्धता" },
        { value: "emotional_distance", label: "भावनात्मक दूरी" },
        { value: "trust", label: "विश्वास" },
        { value: "future_direction", label: "भविष्य की दिशा" },
      ],
      revealButton: "मेरी रीडिंग दिखाएं",
      fields: {
        rhythmBetween: "आपके बीच की लय",
        harmonyLevel: "सामंजस्य स्तर",
        frictionPoint: "एक कोमल अंतर बिंदु",
        timingAlignment: "समय संरेखण",
        connectionPath: "गहरे जुड़ाव की राह",
      },
    },
    vivah: {
      tabLabel: "विवाह मुहूर्त",
      tabSublabel: "समय",
      loading: "आपकी समय-खिड़की पढ़ी जा रही है…",
      resultTitle: "आपकी समय-खिड़की",
      step1Question: "हमें वधू और वर के बारे में बताएं",
      step1Helper: "हम दोनों का विवरण एक बार, पहले ही ले लेते हैं, ताकि हमें दोबारा न पूछना पड़े।",
      brideLabel: "वधू",
      groomLabel: "वर",
      detailsSuffix: " का विवरण",
      namePlaceholder: (person) => `${person} का नाम`,
      dobPlaceholder: "जन्म तिथि — DD/MM/YYYY",
      timePlaceholder: "जन्म समय (वैकल्पिक) — जैसे 10:30 AM",
      placePlaceholder: "जन्म स्थान (वैकल्पिक)",
      step2Question: "वधू इस मिलन के लिए कितनी तैयार महसूस करती है?",
      step3Question: "वर कितना तैयार महसूस करता है?",
      readinessLow: "अभी नहीं",
      readinessHigh: "पूरी तरह तैयार",
      step4Question: "उनके बीच अभी की लय कैसी है?",
      rhythmOptions: [
        { value: "steady", label: "स्थिर और शांत" },
        { value: "intense", label: "गहरा और भावुक" },
        { value: "uncertain", label: "अनिश्चित" },
        { value: "growing", label: "धीरे-धीरे बढ़ता हुआ" },
      ],
      preferredTimingLabel: "पसंदीदा समय",
      periodPlaceholder: "एक अवधि चुनें",
      periodOptions: [
        { value: "next_3_months", label: "अगले 3 महीने" },
        { value: "next_6_months", label: "अगले 6 महीने" },
        { value: "this_year", label: "इस साल" },
        { value: "next_year", label: "अगले साल" },
      ],
      revealButton: "मेरा समय दिखाएं",
      fields: {
        compatibilitySnapshot: "अनुकूलता झलक",
        recommendedWindows: "अनुशंसित समय-खिड़कियां",
        timingToApproachGently: "धीरे से आगे बढ़ने का समय",
      },
    },
    upay: {
      tabLabel: "उपाय मार्ग",
      tabSublabel: "मार्गदर्शन",
      loading: "आपके लिए उपाय खोजे जा रहे हैं…",
      resultTitle: "आपका संरेखण मार्ग",
      step1Question: "अभी आप पर क्या भार है?",
      situationOptions: [
        { value: "stress", label: "तनाव" },
        { value: "overthinking", label: "अत्यधिक सोचना" },
        { value: "career_block", label: "करियर में रुकावट" },
        { value: "relationship_tension", label: "रिश्ते में तनाव" },
        { value: "grief", label: "दुःख" },
        { value: "transition", label: "जीवन परिवर्तन" },
        { value: "loneliness", label: "अकेलापन" },
        { value: "burnout", label: "थकान" },
      ],
      step2Question: "यह कितना भारी महसूस होता है?",
      weightLow: "हल्का",
      weightHigh: "बहुत भारी",
      step3Question: "आप क्या महसूस करना चाहते हैं?",
      outcomeOptions: [
        { value: "clarity", label: "स्पष्टता" },
        { value: "calm", label: "शांति" },
        { value: "release", label: "मुक्ति" },
        { value: "direction", label: "दिशा" },
        { value: "connection", label: "जुड़ाव" },
      ],
      revealButton: "मेरा मार्गदर्शन दिखाएं",
      fields: {
        currentEnergy: "वर्तमान ऊर्जा",
        vedicReflection: "वैदिक चिंतन",
        suggestedPractices: "सुझाए गए अभ्यास",
      },
    },
    bhavna: {
      tabLabel: "भावना दृष्टि",
      tabSublabel: "भावना मानचित्र",
      loading: "आपका आंतरिक मौसम पढ़ा जा रहा है…",
      resultTitle: "आपका आंतरिक मौसम",
      step1Question: "अभी आप कैसा महसूस कर रहे हैं?",
      step2Question: "यह भावना कितनी तीव्र है?",
      intensityLow: "हल्की",
      intensityHigh: "अत्यधिक",
      step3Question: "यह कहाँ महसूस होती है?",
      locationOptions: [
        { value: "mind", label: "मन" },
        { value: "heart", label: "हृदय" },
        { value: "both", label: "दोनों" },
      ],
      revealButton: "मेरा भावना मानचित्र दिखाएं",
      fields: {
        emotionalState: "भावनात्मक स्थिति",
        rootPattern: "मूल पैटर्न",
        currentWeight: "वर्तमान भार",
        innerRoom: "भीतरी कक्ष",
        softLanding: "कोमल विश्राम",
        actions: "कोमल कदम",
      },
    },
    vyaktitva: {
      tabLabel: "व्यक्तित्व दर्शन",
      tabSublabel: "व्यक्तित्व",
      loading: "आपके व्यक्तित्व पैटर्न को पढ़ा जा रहा है…",
      resultTitle: "आपकी व्यक्तित्व प्रोफ़ाइल",
      step1Question: "आप खुद को कैसे बताएंगे?",
      yourTraits: [
        { value: "expressive", label: "अभिव्यंजक" },
        { value: "reserved", label: "संकोची" },
        { value: "independent", label: "स्वतंत्र" },
        { value: "caring", label: "देखभाल करने वाला" },
        { value: "driven", label: "उत्साही" },
      ],
      step2Question: "आप उन्हें कैसे बताएंगे?",
      theirTraits: [
        { value: "warm", label: "गर्मजोशी भरा" },
        { value: "logical", label: "तार्किक" },
        { value: "playful", label: "चंचल" },
        { value: "guarded", label: "सतर्क" },
        { value: "intense", label: "गहन" },
      ],
      step3Question: "आपकी सामान्य बातचीत शैली क्या है?",
      stylePlaceholder: "एक शैली चुनें",
      styleOptions: [
        { value: "direct", label: "सीधा और ईमानदार" },
        { value: "gentle", label: "कोमल और सावधान" },
        { value: "playful", label: "चंचल और हल्का" },
        { value: "deep", label: "गहरा और चिंतनशील" },
      ],
      revealButton: "मेरा व्यक्तित्व दिखाएं",
      fields: {
        coreNature: "मूल स्वभाव",
        emotionalPattern: "भावनात्मक पैटर्न",
        innerRhythm: "आंतरिक लय",
        relationshipStyle: "रिश्ते की शैली",
        growthInvitation: "विकास का निमंत्रण",
        actions: "कोमल कदम",
      },
    },
    samay: {
      tabLabel: "समय प्रवाह",
      tabSublabel: "प्रवाह",
      loading: "आपका प्रवाह पढ़ा जा रहा है…",
      resultTitle: "आपका प्रवाह",
      step1Question: "आपका वर्तमान प्रवाह कैसा महसूस होता है?",
      flowOptions: [
        { value: "rising", label: "उभरता हुआ" },
        { value: "settling", label: "स्थिर होता हुआ" },
        { value: "scattered", label: "बिखरा हुआ" },
        { value: "unclear", label: "निश्चित नहीं" },
      ],
      step2Question: "हाल ही में आप दूसरों से कितना जुड़ाव महसूस कर रहे हैं?",
      commsLow: "अलग-थलग",
      commsHigh: "गहराई से जुड़ा हुआ",
      step3Question: "आपकी ऊर्जा किस दिशा में बढ़ रही है?",
      directionOptions: [
        { value: "inward", label: "अंदर की ओर मुड़ना" },
        { value: "outward", label: "बाहर की ओर पहुंचना" },
        { value: "steady", label: "स्थिर बने रहना" },
      ],
      revealButton: "मेरा प्रवाह दिखाएं",
    },
    aapkaNote: {
      tabLabel: "आपका नोट",
      tabSublabel: "चिंतन",
      loading: "आपके नोट के साथ बैठा जा रहा है…",
      resultTitle: "आपका चिंतन",
      step1Question: "आपके मन में क्या है?",
      step1HelperCaptured: "हमने आपका नवीनतम संदेश ले लिया है — बेझिझक इसे संपादित करें।",
      step1HelperEmpty: "आप जिस पर चिंतन करना चाहते हैं, उसके बारे में कुछ शब्द लिखें।",
      notePlaceholder: "अपना नोट लिखें या संपादित करें…",
      step2Question: "इसके बारे में आप कैसा महसूस करते हैं?",
      revealButton: "मेरा चिंतन दिखाएं",
      reflectOnSomethingElse: "किसी और विषय पर चिंतन करें",
      yourNoteLabel: "आपका नोट",
      yourNoteFallback: "आपके विचारों पर चिंतन",
      reflectionLabel: "चिंतन",
      emotionalThreadLabel: "भावनात्मक धागा",
      gentleNoteLabel: "कोमल संदेश",
    },
  },

  // ─────────────────────────────────────────── TAMIL ─────────────────────
  ta: {
    shell: {
      title: "அஸ்திரியா இந்தியா",
      subtitle: "வேத ஞானத்தில் வேரூன்றிய ஒரு உணர்வுப்பூர்வ வழிகாட்டல் இடம்",
      langToggleLabel: "மொழி",
    },
    shared: {
      stepOf: (step, total) => `படி ${step} / ${total}`,
      percentComplete: (pct) => `${pct}% முடிந்தது`,
      back: "பின்",
      continueLabel: "தொடரவும்",
      selectAnOption: "ஒரு விருப்பத்தைத் தேர்ந்தெடுக்கவும்",
      tryAgain: "மீண்டும் முயற்சிக்கவும்",
      continueInChat: "அரட்டையில் தொடரவும்",
      whatsApp: "வாட்ஸ்அப்",
      instagram: "இன்ஸ்டாகிராம்",
      copy: "நகலெடு",
      copied: "நகலெடுக்கப்பட்டது",
      saveImage: "படத்தை சேமிக்கவும்",
      startNewReading: "புதிய வாசிப்பைத் தொடங்கவும்",
      emojiLabels: {
        content: "திருப்தி",
        low: "தாழ்வு",
        anxious: "கவலை",
        frustrated: "விரக்தி",
        calm: "அமைதி",
        sad: "சோகம்",
        drained: "சோர்வு",
        confused: "குழப்பம்",
      },
    },
    sambandh: {
      tabLabel: "சம்பந்த தாள் மேல்",
      tabSublabel: "உறவு",
      loading: "உங்கள் உறவின் தாளம் படிக்கப்படுகிறது…",
      resultTitle: "உங்கள் உறவின் தாளம்",
      connectionTypeLabel: "தொடர்பு வகை",
      connectionTypePlaceholder: "ஒரு தொடர்பு வகையைத் தேர்ந்தெடுக்கவும்",
      connectionTypes: [
        { value: "partner", label: "துணை / வாழ்க்கைத் துணை" },
        { value: "boyfriend_girlfriend", label: "காதலன் / காதலி" },
        { value: "close_friend", label: "நெருங்கிய நண்பர்" },
        { value: "family", label: "குடும்ப உறுப்பினர்" },
        { value: "situationship", label: "சிக்கலான உறவு" },
      ],
      theirDetailsLabel: "அவர்களின் விவரங்கள்",
      namePlaceholder: "அவர்களின் பெயர்",
      dobPlaceholder: "பிறந்த தேதி — DD/MM/YYYY",
      timePlaceholder: "பிறந்த நேரம் (விருப்பத்தேர்வு) — எ.கா. 10:30 AM",
      placePlaceholder: "பிறந்த இடம் (விருப்பத்தேர்வு)",
      step1Question: "உங்கள் இருவரைப் பற்றி எங்களிடம் கூறுங்கள்",
      step1Helper: "அவர்களின் விவரங்களை ஒரு முறை முன்கூட்டியே சேகரிக்கிறோம், மீண்டும் கேட்க வேண்டாம்.",
      step2Question: "நீங்கள் இருவரும் எவ்வளவு அடிக்கடி பேசுவீர்கள்?",
      step2Low: "அரிதாக",
      step2High: "தொடர்ந்து",
      step3Question: "உங்களுடன் அவர்களின் வழக்கமான தொனி என்ன?",
      toneOptions: [
        { value: "warm", label: "அன்பான" },
        { value: "distant", label: "தூரமான" },
        { value: "unpredictable", label: "ஊகிக்க முடியாத" },
        { value: "steady", label: "நிலையான" },
        { value: "guarded", label: "எச்சரிக்கையான" },
      ],
      step4Question: "எதைப் பற்றி தெளிவு வேண்டும்?",
      step4Helper: "பொருந்தும் அனைத்தையும் தேர்ந்தெடுக்கவும்.",
      clarityOptions: [
        { value: "communication", label: "தொடர்பு" },
        { value: "commitment", label: "அர்ப்பணிப்பு" },
        { value: "emotional_distance", label: "உணர்வுபூர்வ தூரம்" },
        { value: "trust", label: "நம்பிக்கை" },
        { value: "future_direction", label: "எதிர்கால திசை" },
      ],
      revealButton: "எனது வாசிப்பைக் காட்டு",
      fields: {
        rhythmBetween: "உங்களுக்கிடையேயான தாளம்",
        harmonyLevel: "இணக்க நிலை",
        frictionPoint: "ஒரு மென்மையான வேறுபாட்டு புள்ளி",
        timingAlignment: "நேர சீரமைப்பு",
        connectionPath: "ஆழமான தொடர்பின் பாதை",
      },
    },
    vivah: {
      tabLabel: "விவாஹ் முஹூர்த்",
      tabSublabel: "நேரம்",
      loading: "உங்கள் நேர சாளரம் படிக்கப்படுகிறது…",
      resultTitle: "உங்கள் நேர சாளரம்",
      step1Question: "மணமகள் மற்றும் மணமகனைப் பற்றி எங்களிடம் கூறுங்கள்",
      step1Helper: "இருவரின் விவரங்களையும் ஒரு முறை முன்கூட்டியே சேகரிக்கிறோம், மீண்டும் கேட்க வேண்டாம்.",
      brideLabel: "மணமகள்",
      groomLabel: "மணமகன்",
      detailsSuffix: " இன் விவரங்கள்",
      namePlaceholder: (person) => `${person} இன் பெயர்`,
      dobPlaceholder: "பிறந்த தேதி — DD/MM/YYYY",
      timePlaceholder: "பிறந்த நேரம் (விருப்பத்தேர்வு) — எ.கா. 10:30 AM",
      placePlaceholder: "பிறந்த இடம் (விருப்பத்தேர்வு)",
      step2Question: "இந்த திருமணத்திற்கு மணமகள் எவ்வளவு தயாராக உணர்கிறார்?",
      step3Question: "மணமகன் எவ்வளவு தயாராக உணர்கிறார்?",
      readinessLow: "இன்னும் இல்லை",
      readinessHigh: "முழுமையாகத் தயார்",
      step4Question: "இவர்களுக்கிடையேயான தற்போதைய தாளம் என்ன?",
      rhythmOptions: [
        { value: "steady", label: "நிலையான & அமைதியான" },
        { value: "intense", label: "தீவிரமான & ஆர்வமுள்ள" },
        { value: "uncertain", label: "நிச்சயமற்ற" },
        { value: "growing", label: "மெதுவாக வளர்ந்து வரும்" },
      ],
      preferredTimingLabel: "விருப்பமான நேரம்",
      periodPlaceholder: "ஒரு காலகட்டத்தைத் தேர்ந்தெடுக்கவும்",
      periodOptions: [
        { value: "next_3_months", label: "அடுத்த 3 மாதங்கள்" },
        { value: "next_6_months", label: "அடுத்த 6 மாதங்கள்" },
        { value: "this_year", label: "இந்த ஆண்டு" },
        { value: "next_year", label: "அடுத்த ஆண்டு" },
      ],
      revealButton: "எனது நேரத்தைக் காட்டு",
      fields: {
        compatibilitySnapshot: "பொருத்த சுருக்கம்",
        recommendedWindows: "பரிந்துரைக்கப்பட்ட நேர சாளரங்கள்",
        timingToApproachGently: "மென்மையாக அணுக வேண்டிய நேரம்",
      },
    },
    upay: {
      tabLabel: "உபாய் மார்க்",
      tabSublabel: "வழிகாட்டல்",
      loading: "உங்களுக்கான நடைமுறைகள் தேடப்படுகின்றன…",
      resultTitle: "உங்கள் சீரமைப்பு பாதை",
      step1Question: "இப்போது உங்களை எது சுமையாக உணர வைக்கிறது?",
      situationOptions: [
        { value: "stress", label: "மன அழுத்தம்" },
        { value: "overthinking", label: "அதிக சிந்தனை" },
        { value: "career_block", label: "தொழில் தடை" },
        { value: "relationship_tension", label: "உறவு பதற்றம்" },
        { value: "grief", label: "துயரம்" },
        { value: "transition", label: "வாழ்க்கை மாற்றம்" },
        { value: "loneliness", label: "தனிமை" },
        { value: "burnout", label: "சோர்வு" },
      ],
      step2Question: "இது எவ்வளவு கனமாக உணர்கிறது?",
      weightLow: "இலகுவான",
      weightHigh: "மிகவும் கனமான",
      step3Question: "நீங்கள் எதை உணர விரும்புகிறீர்கள்?",
      outcomeOptions: [
        { value: "clarity", label: "தெளிவு" },
        { value: "calm", label: "அமைதி" },
        { value: "release", label: "விடுதலை" },
        { value: "direction", label: "திசை" },
        { value: "connection", label: "தொடர்பு" },
      ],
      revealButton: "எனது வழிகாட்டலைக் காட்டு",
      fields: {
        currentEnergy: "தற்போதைய ஆற்றல்",
        vedicReflection: "வேத சிந்தனை",
        suggestedPractices: "பரிந்துரைக்கப்பட்ட நடைமுறைகள்",
      },
    },
    bhavna: {
      tabLabel: "பாவனா திருஷ்டி",
      tabSublabel: "உணர்வு வரைபடம்",
      loading: "உங்கள் அக வானிலை படிக்கப்படுகிறது…",
      resultTitle: "உங்கள் அக வானிலை",
      step1Question: "இப்போது நீங்கள் எப்படி உணர்கிறீர்கள்?",
      step2Question: "இந்த உணர்வு எவ்வளவு தீவிரமானது?",
      intensityLow: "லேசான",
      intensityHigh: "மிகைப்படுத்தும்",
      step3Question: "இது எங்கே அமைந்துள்ளது?",
      locationOptions: [
        { value: "mind", label: "மனம்" },
        { value: "heart", label: "இதயம்" },
        { value: "both", label: "இரண்டும்" },
      ],
      revealButton: "எனது உணர்வு வரைபடத்தைக் காட்டு",
      fields: {
        emotionalState: "உணர்வு நிலை",
        rootPattern: "வேர் அமைப்பு",
        currentWeight: "தற்போதைய சுமை",
        innerRoom: "அக அறை",
        softLanding: "மென்மையான தரையிறங்கல்",
        actions: "மென்மையான படிகள்",
      },
    },
    vyaktitva: {
      tabLabel: "வ்யக்திதவ தர்ஷன்",
      tabSublabel: "ஆளுமை",
      loading: "உங்கள் ஆளுமை அமைப்பு படிக்கப்படுகிறது…",
      resultTitle: "உங்கள் ஆளுமை விவரக்குறிப்பு",
      step1Question: "உங்களை நீங்கள் எப்படி விவரிப்பீர்கள்?",
      yourTraits: [
        { value: "expressive", label: "வெளிப்படையான" },
        { value: "reserved", label: "அடக்கமான" },
        { value: "independent", label: "சுதந்திரமான" },
        { value: "caring", label: "அக்கறையுள்ள" },
        { value: "driven", label: "உந்துதலுள்ள" },
      ],
      step2Question: "அவர்களை நீங்கள் எப்படி விவரிப்பீர்கள்?",
      theirTraits: [
        { value: "warm", label: "அன்பான" },
        { value: "logical", label: "தர்க்கரீதியான" },
        { value: "playful", label: "விளையாட்டுத்தனமான" },
        { value: "guarded", label: "எச்சரிக்கையான" },
        { value: "intense", label: "தீவிரமான" },
      ],
      step3Question: "உங்கள் வழக்கமான தொடர்பு பாணி என்ன?",
      stylePlaceholder: "ஒரு பாணியைத் தேர்ந்தெடுக்கவும்",
      styleOptions: [
        { value: "direct", label: "நேரடியான & நேர்மையான" },
        { value: "gentle", label: "மென்மையான & கவனமான" },
        { value: "playful", label: "விளையாட்டுத்தனமான & இலகுவான" },
        { value: "deep", label: "ஆழமான & சிந்தனையுள்ள" },
      ],
      revealButton: "எனது ஆளுமையைக் காட்டு",
      fields: {
        coreNature: "அடிப்படை இயல்பு",
        emotionalPattern: "உணர்வு அமைப்பு",
        innerRhythm: "அக தாளம்",
        relationshipStyle: "உறவு பாணி",
        growthInvitation: "வளர்ச்சி அழைப்பு",
        actions: "மென்மையான படிகள்",
      },
    },
    samay: {
      tabLabel: "சமய் ப்ரவாஹ்",
      tabSublabel: "ஓட்டம்",
      loading: "உங்கள் ஓட்டம் படிக்கப்படுகிறது…",
      resultTitle: "உங்கள் ஓட்டம்",
      step1Question: "உங்கள் தற்போதைய ஓட்டம் எப்படி உணர்கிறது?",
      flowOptions: [
        { value: "rising", label: "எழுச்சி" },
        { value: "settling", label: "தேங்குதல்" },
        { value: "scattered", label: "சிதறிய" },
        { value: "unclear", label: "உறுதியாக இல்லை" },
      ],
      step2Question: "சமீபத்தில் மற்றவர்களுடன் நீங்கள் எவ்வளவு இணைந்து உணர்ந்தீர்கள்?",
      commsLow: "விலகிய",
      commsHigh: "மிகவும் இணைந்த",
      step3Question: "உங்கள் ஆற்றல் எந்த திசையில் நகர்கிறது?",
      directionOptions: [
        { value: "inward", label: "உள்நோக்கி திரும்புதல்" },
        { value: "outward", label: "வெளிநோக்கி நீட்டுதல்" },
        { value: "steady", label: "நிலையாக இருத்தல்" },
      ],
      revealButton: "எனது ஓட்டத்தைக் காட்டு",
    },
    aapkaNote: {
      tabLabel: "ஆப்கா நோட்",
      tabSublabel: "பிரதிபலிப்பு",
      loading: "உங்கள் குறிப்புடன் அமர்ந்திருக்கிறோம்…",
      resultTitle: "உங்கள் பிரதிபலிப்பு",
      step1Question: "உங்கள் மனதில் என்ன உள்ளது?",
      step1HelperCaptured: "உங்கள் சமீபத்திய செய்தியை எடுத்துக்கொண்டோம் — தயங்காமல் திருத்தவும்.",
      step1HelperEmpty: "நீங்கள் சிந்திக்க விரும்புவதைப் பற்றி சில வார்த்தைகள் எழுதுங்கள்.",
      notePlaceholder: "உங்கள் குறிப்பை தட்டச்சு செய்யவும் அல்லது திருத்தவும்…",
      step2Question: "இதைப் பற்றி நீங்கள் எப்படி உணர்கிறீர்கள்?",
      revealButton: "எனது பிரதிபலிப்பைக் காட்டு",
      reflectOnSomethingElse: "வேறு ஏதாவது பற்றி சிந்தியுங்கள்",
      yourNoteLabel: "உங்கள் குறிப்பு",
      yourNoteFallback: "உங்கள் எண்ணங்கள் பற்றிய பிரதிபலிப்பு",
      reflectionLabel: "பிரதிபலிப்பு",
      emotionalThreadLabel: "உணர்வு இழை",
      gentleNoteLabel: "மென்மையான குறிப்பு",
    },
  },

  // ─────────────────────────────────────────── MARATHI ───────────────────
  mr: {
    shell: {
      title: "अस्त्रिया इंडिया",
      subtitle: "वैदिक ज्ञानात रुजलेली एक भावनिक मार्गदर्शन जागा",
      langToggleLabel: "भाषा",
    },
    shared: {
      stepOf: (step, total) => `पायरी ${step} / ${total}`,
      percentComplete: (pct) => `${pct}% पूर्ण`,
      back: "मागे",
      continueLabel: "पुढे सुरू ठेवा",
      selectAnOption: "एक पर्याय निवडा",
      tryAgain: "पुन्हा प्रयत्न करा",
      continueInChat: "चॅटमध्ये सुरू ठेवा",
      whatsApp: "व्हॉट्सअ‍ॅप",
      instagram: "इंस्टाग्राम",
      copy: "कॉपी करा",
      copied: "कॉपी झाले",
      saveImage: "प्रतिमा जतन करा",
      startNewReading: "नवीन वाचन सुरू करा",
      emojiLabels: {
        content: "समाधानी",
        low: "उदास",
        anxious: "चिंताग्रस्त",
        frustrated: "निराश",
        calm: "शांत",
        sad: "दुःखी",
        drained: "थकलेले",
        confused: "गोंधळलेले",
      },
    },
    sambandh: {
      tabLabel: "संबंध ताल मेल",
      tabSublabel: "नाते",
      loading: "तुमच्या नात्याची लय वाचली जात आहे…",
      resultTitle: "तुमच्या नात्याची लय",
      connectionTypeLabel: "नात्याचा प्रकार",
      connectionTypePlaceholder: "एक नात्याचा प्रकार निवडा",
      connectionTypes: [
        { value: "partner", label: "जोडीदार / पती-पत्नी" },
        { value: "boyfriend_girlfriend", label: "बॉयफ्रेंड / गर्लफ्रेंड" },
        { value: "close_friend", label: "जवळचा मित्र" },
        { value: "family", label: "कुटुंबातील सदस्य" },
        { value: "situationship", label: "थोडं गुंतागुंतीचं नातं" },
      ],
      theirDetailsLabel: "त्यांचे तपशील",
      namePlaceholder: "त्यांचे नाव",
      dobPlaceholder: "जन्मतारीख — DD/MM/YYYY",
      timePlaceholder: "जन्मवेळ (ऐच्छिक) — उदा. 10:30 AM",
      placePlaceholder: "जन्मस्थान (ऐच्छिक)",
      step1Question: "आम्हाला तुम्हा दोघांबद्दल सांगा",
      step1Helper: "आम्ही त्यांचे तपशील एकदाच, आधीच घेतो, जेणेकरून पुन्हा विचारावे लागणार नाही.",
      step2Question: "तुम्ही दोघे किती वेळा बोलता?",
      step2Low: "क्वचितच",
      step2High: "सतत",
      step3Question: "तुमच्यासोबत त्यांचा नेहमीचा स्वर कसा असतो?",
      toneOptions: [
        { value: "warm", label: "उबदार" },
        { value: "distant", label: "अंतर राखणारे" },
        { value: "unpredictable", label: "अनिश्चित" },
        { value: "steady", label: "स्थिर" },
        { value: "guarded", label: "सावध" },
      ],
      step4Question: "तुम्हाला कशाबद्दल स्पष्टता हवी आहे?",
      step4Helper: "जे लागू होईल ते सर्व निवडा.",
      clarityOptions: [
        { value: "communication", label: "संवाद" },
        { value: "commitment", label: "बांधिलकी" },
        { value: "emotional_distance", label: "भावनिक अंतर" },
        { value: "trust", label: "विश्वास" },
        { value: "future_direction", label: "भविष्यातील दिशा" },
      ],
      revealButton: "माझं वाचन दाखवा",
      fields: {
        rhythmBetween: "तुमच्यामधील लय",
        harmonyLevel: "सुसंवाद पातळी",
        frictionPoint: "एक सौम्य मतभेदाचा मुद्दा",
        timingAlignment: "वेळेचे संरेखन",
        connectionPath: "सखोल जोडणीचा मार्ग",
      },
    },
    vivah: {
      tabLabel: "विवाह मुहूर्त",
      tabSublabel: "वेळ",
      loading: "तुमची वेळ-खिडकी वाचली जात आहे…",
      resultTitle: "तुमची वेळ-खिडकी",
      step1Question: "आम्हाला वधू आणि वराबद्दल सांगा",
      step1Helper: "आम्ही दोघांचेही तपशील एकदाच, आधीच घेतो, जेणेकरून पुन्हा विचारावे लागणार नाही.",
      brideLabel: "वधू",
      groomLabel: "वर",
      detailsSuffix: "चे तपशील",
      namePlaceholder: (person) => `${person}चे नाव`,
      dobPlaceholder: "जन्मतारीख — DD/MM/YYYY",
      timePlaceholder: "जन्मवेळ (ऐच्छिक) — उदा. 10:30 AM",
      placePlaceholder: "जन्मस्थान (ऐच्छिक)",
      step2Question: "वधू या मिलनासाठी किती तयार आहे असे वाटते?",
      step3Question: "वर किती तयार आहे असे वाटते?",
      readinessLow: "अजून नाही",
      readinessHigh: "पूर्णपणे तयार",
      step4Question: "त्यांच्यामधील सध्याची लय कशी आहे?",
      rhythmOptions: [
        { value: "steady", label: "स्थिर आणि शांत" },
        { value: "intense", label: "तीव्र आणि उत्कट" },
        { value: "uncertain", label: "अनिश्चित" },
        { value: "growing", label: "हळूहळू वाढणारे" },
      ],
      preferredTimingLabel: "पसंतीची वेळ",
      periodPlaceholder: "एक कालावधी निवडा",
      periodOptions: [
        { value: "next_3_months", label: "पुढील 3 महिने" },
        { value: "next_6_months", label: "पुढील 6 महिने" },
        { value: "this_year", label: "यंदाचे वर्ष" },
        { value: "next_year", label: "पुढील वर्ष" },
      ],
      revealButton: "माझी वेळ दाखवा",
      fields: {
        compatibilitySnapshot: "सुसंगतता झलक",
        recommendedWindows: "शिफारस केलेल्या वेळ-खिडक्या",
        timingToApproachGently: "हळुवारपणे पुढे जाण्याची वेळ",
      },
    },
    upay: {
      tabLabel: "उपाय मार्ग",
      tabSublabel: "मार्गदर्शन",
      loading: "तुमच्यासाठी उपाय शोधले जात आहेत…",
      resultTitle: "तुमचा संरेखन मार्ग",
      step1Question: "सध्या तुमच्यावर कशाचे ओझे आहे?",
      situationOptions: [
        { value: "stress", label: "ताण" },
        { value: "overthinking", label: "अति विचार" },
        { value: "career_block", label: "करिअर अडथळा" },
        { value: "relationship_tension", label: "नात्यातील तणाव" },
        { value: "grief", label: "दुःख" },
        { value: "transition", label: "जीवन बदल" },
        { value: "loneliness", label: "एकटेपणा" },
        { value: "burnout", label: "थकवा" },
      ],
      step2Question: "हे किती जड वाटते?",
      weightLow: "हलके",
      weightHigh: "खूप जड",
      step3Question: "तुम्हाला काय वाटावे असे हवे आहे?",
      outcomeOptions: [
        { value: "clarity", label: "स्पष्टता" },
        { value: "calm", label: "शांतता" },
        { value: "release", label: "मुक्तता" },
        { value: "direction", label: "दिशा" },
        { value: "connection", label: "जोडणी" },
      ],
      revealButton: "माझं मार्गदर्शन दाखवा",
      fields: {
        currentEnergy: "सध्याची ऊर्जा",
        vedicReflection: "वैदिक चिंतन",
        suggestedPractices: "सुचवलेले सराव",
      },
    },
    bhavna: {
      tabLabel: "भावना दृष्टी",
      tabSublabel: "भावना नकाशा",
      loading: "तुमचे आंतरिक हवामान वाचले जात आहे…",
      resultTitle: "तुमचे आंतरिक हवामान",
      step1Question: "सध्या तुम्हाला कसे वाटत आहे?",
      step2Question: "ही भावना किती तीव्र आहे?",
      intensityLow: "सौम्य",
      intensityHigh: "अतिप्रचंड",
      step3Question: "हे कुठे जाणवते?",
      locationOptions: [
        { value: "mind", label: "मन" },
        { value: "heart", label: "हृदय" },
        { value: "both", label: "दोन्ही" },
      ],
      revealButton: "माझा भावना नकाशा दाखवा",
      fields: {
        emotionalState: "भावनिक स्थिती",
        rootPattern: "मूळ नमुना",
        currentWeight: "सध्याचे ओझे",
        innerRoom: "आंतरिक कक्ष",
        softLanding: "सौम्य विसावा",
        actions: "सौम्य पावले",
      },
    },
    vyaktitva: {
      tabLabel: "व्यक्तित्व दर्शन",
      tabSublabel: "व्यक्तिमत्त्व",
      loading: "तुमच्या व्यक्तिमत्त्वाचा नमुना वाचला जात आहे…",
      resultTitle: "तुमची व्यक्तिमत्त्व प्रोफाइल",
      step1Question: "तुम्ही स्वतःचे वर्णन कसे कराल?",
      yourTraits: [
        { value: "expressive", label: "अभिव्यक्त" },
        { value: "reserved", label: "अबोल" },
        { value: "independent", label: "स्वतंत्र" },
        { value: "caring", label: "काळजी घेणारे" },
        { value: "driven", label: "प्रेरित" },
      ],
      step2Question: "तुम्ही त्यांचे वर्णन कसे कराल?",
      theirTraits: [
        { value: "warm", label: "उबदार" },
        { value: "logical", label: "तर्कशुद्ध" },
        { value: "playful", label: "खेळकर" },
        { value: "guarded", label: "सावध" },
        { value: "intense", label: "तीव्र" },
      ],
      step3Question: "तुमची नेहमीची संवाद शैली काय आहे?",
      stylePlaceholder: "एक शैली निवडा",
      styleOptions: [
        { value: "direct", label: "थेट आणि प्रामाणिक" },
        { value: "gentle", label: "सौम्य आणि काळजीपूर्वक" },
        { value: "playful", label: "खेळकर आणि हलकं" },
        { value: "deep", label: "खोल आणि चिंतनशील" },
      ],
      revealButton: "माझं व्यक्तिमत्त्व दाखवा",
      fields: {
        coreNature: "मूळ स्वभाव",
        emotionalPattern: "भावनिक नमुना",
        innerRhythm: "आंतरिक लय",
        relationshipStyle: "नात्याची शैली",
        growthInvitation: "वाढीचे आमंत्रण",
        actions: "सौम्य पावले",
      },
    },
    samay: {
      tabLabel: "समय प्रवाह",
      tabSublabel: "प्रवाह",
      loading: "तुमचा प्रवाह वाचला जात आहे…",
      resultTitle: "तुमचा प्रवाह",
      step1Question: "तुमचा सध्याचा प्रवाह कसा वाटतो?",
      flowOptions: [
        { value: "rising", label: "उभरता" },
        { value: "settling", label: "स्थिरावणारा" },
        { value: "scattered", label: "विखुरलेला" },
        { value: "unclear", label: "निश्चित नाही" },
      ],
      step2Question: "अलीकडे इतरांशी तुम्ही किती जोडलेले वाटलात?",
      commsLow: "अलिप्त",
      commsHigh: "खूप जोडलेले",
      step3Question: "तुमची ऊर्जा कोणत्या दिशेने जात आहे?",
      directionOptions: [
        { value: "inward", label: "आतल्या दिशेने वळणे" },
        { value: "outward", label: "बाहेरच्या दिशेने पोहोचणे" },
        { value: "steady", label: "स्थिर राहणे" },
      ],
      revealButton: "माझा प्रवाह दाखवा",
    },
    aapkaNote: {
      tabLabel: "आपका नोट",
      tabSublabel: "चिंतन",
      loading: "तुमच्या नोंदीसह बसत आहोत…",
      resultTitle: "तुमचे चिंतन",
      step1Question: "तुमच्या मनात काय आहे?",
      step1HelperCaptured: "आम्ही तुमचा नवीनतम संदेश घेतला आहे — बिनधास्त संपादित करा.",
      step1HelperEmpty: "ज्यावर तुम्हाला चिंतन करायचे आहे त्याबद्दल काही शब्द लिहा.",
      notePlaceholder: "तुमची नोंद टाइप करा किंवा संपादित करा…",
      step2Question: "याबद्दल तुम्हाला कसे वाटते?",
      revealButton: "माझं चिंतन दाखवा",
      reflectOnSomethingElse: "दुसऱ्या एखाद्या गोष्टीवर चिंतन करा",
      yourNoteLabel: "तुमची नोंद",
      yourNoteFallback: "तुमच्या विचारांवरील चिंतन",
      reflectionLabel: "चिंतन",
      emotionalThreadLabel: "भावनिक धागा",
      gentleNoteLabel: "सौम्य संदेश",
    },
  },
};
